import React from 'react';
import { MapPin, Target, Users, Globe2 } from 'lucide-react';
import { ThreatActor } from '@/mock/threatActors';
import { ViewMode } from './ThreatProfileControls';

interface ActorSelectionPanelProps {
    actors: ThreatActor[];
    totalActors: number;
    selectedActor: string | null;
    viewMode: ViewMode;
    onActorSelect: (actorId: string) => void;
}

const ActorSelectionPanel: React.FC<ActorSelectionPanelProps> = ({
    actors,
    totalActors,
    selectedActor,
    viewMode,
    onActorSelect,
}) => {
    const getSeverityStyles = (severity: string) => {
        switch (severity) {
            case 'critical': return 'border-[#FF3333]/40 text-[#FF3333] bg-[#FF3333]/5';
            case 'high': return 'border-[#F97316]/40 text-[#F97316] bg-[#F97316]/5';
            case 'medium': return 'border-[#EAB308]/40 text-[#EAB308] bg-[#EAB308]/5';
            default: return 'border-slate-500/40 text-slate-400 bg-slate-500/5';
        }
    };

    return (
        <div className="w-full h-full bg-[#0A0B0E] flex flex-col font-mono">
            {/* Panel Header */}
            <div className="p-5 border-b border-[#1A1D24] bg-[#050505] flex items-center justify-between flex-shrink-0">
                <div className="flex items-center gap-2">
                    <Users className="w-4 h-4 text-[#02DFC2]" />
                    <h3 className="text-[12px] font-bold text-white tracking-widest uppercase">
                        Adversary_List
                    </h3>
                </div>
                <div className="flex items-center gap-2 px-2 py-0.5 bg-[#1A1D24] rounded text-[10px] text-slate-500 font-bold uppercase tracking-tighter">
                    <span className="text-white">{actors.length}</span>
                    <span>/</span>
                    <span>{totalActors}</span>
                </div>
            </div>

            {/* List Container */}
            <div className="flex-1 overflow-y-auto custom-scrollbar p-4 space-y-3">
                {actors.map((actor) => {
                    const isSelected = selectedActor === actor.id;

                    return (
                        <div
                            key={actor.id}
                            onClick={() => onActorSelect(actor.id)}
                            className={`group relative p-4 border transition-all duration-200 cursor-pointer rounded-xl overflow-hidden ${isSelected
                                    ? 'bg-[#02DFC2]/5 border-[#02DFC2]/50 shadow-[0_0_20px_rgba(2,223,194,0.05)]'
                                    : 'bg-[#050505] border-[#1A1D24] hover:border-slate-600 hover:bg-[#12151C]'
                                }`}
                        >
                            {/* Selection indicator bar */}
                            {isSelected && (
                                <div className="absolute left-0 top-0 bottom-0 w-1 bg-[#02DFC2] shadow-[0_0_10px_#02DFC2]"></div>
                            )}

                            <div className="flex flex-col gap-3">
                                {/* Top Row: Name and Severity */}
                                <div className="flex items-start justify-between gap-2">
                                    <div className="min-w-0">
                                        <h4 className={`text-[13px] font-bold tracking-tight uppercase truncate transition-colors ${isSelected ? 'text-white' : 'text-slate-300 group-hover:text-white'
                                            }`}>
                                            {actor.name}
                                        </h4>
                                        <p className="text-[10px] text-slate-600 font-bold tracking-tighter uppercase mt-0.5">
                                            ID: {actor.id}
                                        </p>
                                    </div>
                                    <span className={`px-2 py-0.5 text-[9px] font-bold uppercase tracking-widest rounded border flex-shrink-0 ${getSeverityStyles(actor.severity)}`}>
                                        {actor.severity}
                                    </span>
                                </div>

                                {/* Extended Info (Only shown in detailed grid-like view) */}
                                {viewMode === 'grid' && (
                                    <div className="space-y-2 border-t border-[#1A1D24] pt-3 animate-in fade-in slide-in-from-top-1">
                                        {actor.aliases.length > 0 && (
                                            <div className="flex flex-wrap gap-1">
                                                {actor.aliases.slice(0, 2).map(alias => (
                                                    <span key={alias} className="text-[9px] bg-[#1A1D24] text-slate-500 px-1.5 py-0.5 rounded border border-transparent group-hover:border-slate-700">
                                                        {alias}
                                                    </span>
                                                ))}
                                            </div>
                                        )}

                                        <div className="flex items-center justify-between text-[10px]">
                                            <div className="flex items-center text-slate-500 uppercase">
                                                <MapPin className="w-3 h-3 mr-1 text-[#02DFC2]/50" />
                                                {actor.country || 'EXFIL_UNKNOWN'}
                                            </div>
                                            {isSelected && (
                                                <div className="flex items-center text-[#02DFC2] font-bold animate-pulse">
                                                    <Target className="w-3 h-3 mr-1" />
                                                    LOCKED
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}

                                {/* Compact view for 'list' mode */}
                                {viewMode === 'list' && (
                                    <div className="flex items-center gap-4 text-[10px] text-slate-600 uppercase">
                                        <div className="flex items-center">
                                            <Globe2 className="w-3 h-3 mr-1" />
                                            {actor.country || 'UNK'}
                                        </div>
                                        {isSelected && <span className="text-[#02DFC2] font-bold ml-auto">[ACTIVE]</span>}
                                    </div>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* Internal CSS for the panel scrollbar */}
            <style dangerouslySetInnerHTML={{
                __html: `
                .custom-scrollbar::-webkit-scrollbar {
                    width: 4px;
                }
                .custom-scrollbar::-webkit-scrollbar-track {
                    background: transparent;
                }
                .custom-scrollbar::-webkit-scrollbar-thumb {
                    background: #1A1D24;
                    border-radius: 10px;
                }
                .custom-scrollbar::-webkit-scrollbar-thumb:hover {
                    background: rgba(2, 223, 194, 0.3);
                }
            `}} />
        </div>
    );
};

export default ActorSelectionPanel;